/**
 * AnalyticsTab - Admin analytics dashboard
 * Displays ONLY real data from the database. No fabrication.
 */
import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  BarChart3, Users, Eye, MousePointerClick, TrendingUp, Download,
  Clock, Film, Radio, Newspaper, Bell, Activity, Loader2, RefreshCw,
  Trophy, CalendarDays, UserPlus
} from 'lucide-react';
import { Button } from '../ui/button';
import { API, authHeaders } from './utils';

const RANGES = [
  { id: 'today', label: 'Today' },
  { id: '7d', label: '7 Days' },
  { id: '30d', label: '30 Days' },
  { id: 'all', label: 'All Time' }
];

const StatCard = ({ icon: Icon, label, value, sub, color = 'orange' }) => {
  const colorMap = {
    orange: 'from-orange-500/20 to-orange-600/5 border-orange-500/30',
    blue: 'from-blue-500/20 to-blue-600/5 border-blue-500/30',
    green: 'from-emerald-500/20 to-emerald-600/5 border-emerald-500/30',
    purple: 'from-violet-500/20 to-violet-600/5 border-violet-500/30',
    red: 'from-red-500/20 to-red-600/5 border-red-500/30',
    cyan: 'from-cyan-500/20 to-cyan-600/5 border-cyan-500/30'
  };
  const iconColor = {
    orange: 'text-orange-400', blue: 'text-blue-400', green: 'text-emerald-400',
    purple: 'text-violet-400', red: 'text-red-400', cyan: 'text-cyan-400'
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      className={`bg-gradient-to-br ${colorMap[color]} border rounded-lg p-4`}
      data-testid={`stat-card-${label.toLowerCase().replace(/\s/g, '-')}`}
    >
      <div className="flex items-center justify-between mb-2">
        <Icon className={`w-5 h-5 ${iconColor[color]}`} />
      </div>
      <p className="text-2xl font-bold text-white">{value}</p>
      <p className="text-xs text-zinc-400 mt-1">{label}</p>
      {sub && <p className="text-[10px] text-zinc-500 mt-0.5">{sub}</p>}
    </motion.div>
  );
};

const MiniBarChart = ({ data }) => {
  if (!data?.length) return null;
  const m = Math.max(...data.map(d => d.value)) || 1;
  return (
    <div className="space-y-1">
      {data.map((d, i) => (
        <div key={i} className="flex items-center gap-2 text-xs">
          <span className="w-20 text-zinc-500 truncate text-right">{d.label}</span>
          <div className="flex-1 bg-zinc-800 rounded-sm h-5 overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-orange-500 to-orange-400 rounded-sm transition-all"
              style={{ width: `${Math.max(2, (d.value / m) * 100)}%` }}
            />
          </div>
          <span className="w-14 text-zinc-400 text-right">{d.value.toLocaleString()}</span>
        </div>
      ))}
    </div>
  );
};

const TrendChart = ({ data, height = 120 }) => {
  if (!data?.length) return <p className="text-zinc-600 text-xs text-center py-8">No trend data available</p>;
  const maxGames = Math.max(...data.map(d => d.games)) || 1;
  const maxUsers = Math.max(...data.map(d => d.new_users)) || 1;
  const maxRec = Math.max(...data.map(d => d.recordings)) || 1;
  const maxVal = Math.max(maxGames, maxUsers, maxRec);

  return (
    <div className="relative" style={{ height }}>
      <div className="absolute inset-0 flex items-end gap-px">
        {data.map((d, i) => {
          const gameH = maxVal > 0 ? (d.games / maxVal) * 100 : 0;
          const userH = maxVal > 0 ? (d.new_users / maxVal) * 100 : 0;
          const recH = maxVal > 0 ? (d.recordings / maxVal) * 100 : 0;
          return (
            <div key={i} className="flex-1 flex items-end gap-px group relative">
              <div className="flex-1 bg-orange-500/60 rounded-t-sm transition-all hover:bg-orange-500/80" style={{ height: `${Math.max(1, gameH)}%` }} />
              <div className="flex-1 bg-blue-500/50 rounded-t-sm transition-all hover:bg-blue-500/70" style={{ height: `${Math.max(1, userH)}%` }} />
              <div className="flex-1 bg-emerald-500/50 rounded-t-sm transition-all hover:bg-emerald-500/70" style={{ height: `${Math.max(1, recH)}%` }} />
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 bg-zinc-800 border border-white/10 px-2 py-1 rounded text-[10px] text-white whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none z-10 transition-opacity">
                <p className="font-medium">{d.date}</p>
                <p className="text-orange-400">Games: {d.games}</p>
                <p className="text-blue-400">New Users: {d.new_users}</p>
                <p className="text-emerald-400">Recordings: {d.recordings}</p>
              </div>
            </div>
          );
        })}
      </div>
      <div className="absolute -bottom-5 left-0 right-0 flex justify-between text-[9px] text-zinc-600">
        <span>{data[0]?.date?.slice(5)}</span>
        <span>{data[Math.floor(data.length / 2)]?.date?.slice(5)}</span>
        <span>{data[data.length - 1]?.date?.slice(5)}</span>
      </div>
    </div>
  );
};

const PeakHoursChart = ({ hours }) => {
  if (!hours?.length) return null;
  const hasData = hours.some(h => h.activity > 0);
  if (!hasData) return <p className="text-zinc-600 text-xs text-center py-8">No broadcast schedule data yet</p>;

  return (
    <div className="flex items-end gap-[2px] h-24">
      {hours.map((h, i) => (
        <div key={i} className="flex-1 flex flex-col items-center group relative">
          <div
            className={`w-full rounded-t-sm transition-all ${h.activity > 0 ? 'bg-gradient-to-t from-cyan-600 to-cyan-400 hover:from-cyan-500 hover:to-cyan-300' : 'bg-zinc-800'}`}
            style={{ height: `${Math.max(3, h.percentage)}%` }}
          />
          <div className="absolute bottom-full mb-1 bg-zinc-800 border border-white/10 px-2 py-1 rounded text-[10px] text-white whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none z-10">
            {h.label} — {h.activity} game{h.activity !== 1 ? 's' : ''}
          </div>
          {i % 4 === 0 && <span className="text-[8px] text-zinc-600 mt-1">{h.label?.slice(0, 2)}</span>}
        </div>
      ))}
    </div>
  );
};

export const AnalyticsTab = () => {
  const [timeRange, setTimeRange] = useState('7d');
  const [overview, setOverview] = useState(null);
  const [trend, setTrend] = useState([]);
  const [topContent, setTopContent] = useState([]);
  const [sportsBreakdown, setSportsBreakdown] = useState([]);
  const [peakHours, setPeakHours] = useState([]);
  const [adPerformance, setAdPerformance] = useState(null);
  const [broadcastSummary, setBroadcastSummary] = useState(null);
  const [realtime, setRealtime] = useState({ logged_in_viewers: 0 });
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    const headers = authHeaders();
    try {
      const days = timeRange === 'today' ? 1 : timeRange === '7d' ? 7 : timeRange === '30d' ? 30 : 90;
      const [ovRes, trendRes, topRes, sportsRes, peakRes, adRes, bcastRes, rtRes] = await Promise.all([
        fetch(`${API}/analytics/overview?time_range=${timeRange}`, { headers }).then(r => r.ok ? r.json() : null),
        fetch(`${API}/analytics/daily-trend?days=${days}`, { headers }).then(r => r.ok ? r.json() : null),
        fetch(`${API}/analytics/content/top?limit=10`, { headers }).then(r => r.ok ? r.json() : null),
        fetch(`${API}/analytics/sports/breakdown`, { headers }).then(r => r.ok ? r.json() : null),
        fetch(`${API}/analytics/peak-hours`, { headers }).then(r => r.ok ? r.json() : null),
        fetch(`${API}/analytics/ad-performance`, { headers }).then(r => r.ok ? r.json() : null),
        fetch(`${API}/analytics/broadcasts-summary`, { headers }).then(r => r.ok ? r.json() : null),
        fetch(`${API}/analytics/viewers/realtime`, { headers }).then(r => r.ok ? r.json() : null)
      ]);
      if (ovRes) setOverview(ovRes);
      if (trendRes) setTrend(trendRes.trend || []);
      if (topRes) setTopContent(topRes.top_content || []);
      if (sportsRes) setSportsBreakdown(sportsRes.sports || []);
      if (peakRes) setPeakHours(peakRes.hours || []);
      if (adRes) setAdPerformance(adRes);
      if (bcastRes) setBroadcastSummary(bcastRes);
      if (rtRes) setRealtime(rtRes);
    } catch (err) {
      console.error('Analytics fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [timeRange]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const fmt = (n) => {
    if (n == null) return '0';
    if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
    if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
    return n.toLocaleString();
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await fetch(`${API}/analytics/export?time_range=${timeRange}`, { headers: authHeaders() });
      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `analytics_${timeRange}.csv`;
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch (err) {
      console.error('Export error:', err);
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="analytics-tab">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-secondary text-2xl font-bold uppercase tracking-tight text-white">Analytics</h2>
          <p className="text-zinc-500 text-sm">Real performance data from your platform</p>
        </div>
        <div className="flex items-center gap-2">
          {RANGES.map(r => (
            <button
              key={r.id}
              onClick={() => setTimeRange(r.id)}
              className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${
                timeRange === r.id ? 'bg-orange-500 text-white' : 'bg-zinc-800 text-zinc-400 hover:text-white'
              }`}
              data-testid={`range-${r.id}`}
            >
              {r.label}
            </button>
          ))}
          <Button size="sm" variant="outline" onClick={handleExport} disabled={exporting} className="text-xs ml-2" data-testid="export-csv-btn">
            {exporting ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Download className="w-3 h-3 mr-1" />}
            Export CSV
          </Button>
          <button onClick={fetchAll} className="p-2 hover:bg-zinc-800 rounded-lg text-zinc-400 hover:text-white">
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Real-time Indicator */}
      <div className="flex items-center gap-3 bg-zinc-900/50 border border-white/5 rounded-lg px-4 py-3">
        <span className="relative flex h-2.5 w-2.5">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
          <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500"></span>
        </span>
        <span className="text-sm text-white font-medium">{fmt(realtime.logged_in_viewers)}</span>
        <span className="text-xs text-zinc-500">active viewer{realtime.logged_in_viewers !== 1 ? 's' : ''} right now</span>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <StatCard icon={Users} label="Total Users" value={fmt(overview?.users?.total)} sub={`${fmt(overview?.users?.new)} new in range`} color="blue" />
        <StatCard icon={Eye} label="VOD Views" value={fmt(overview?.content?.total_vod_views)} sub="All-time total" color="orange" />
        <StatCard icon={MousePointerClick} label="Ad Impressions" value={fmt(overview?.traffic?.ad_impressions)} color="purple" />
        <StatCard icon={MousePointerClick} label="Ad Clicks" value={fmt(overview?.traffic?.ad_total_clicks)} color="green" />
        <StatCard icon={Radio} label="Live Now" value={fmt(broadcastSummary?.broadcasts?.live_now)} sub={`${fmt(broadcastSummary?.broadcasts?.upcoming)} upcoming`} color="red" />
        <StatCard icon={Film} label="Recordings" value={fmt(broadcastSummary?.recordings?.published)} sub={`${fmt(broadcastSummary?.recordings?.total)} total`} color="cyan" />
      </div>

      {/* Content Summary Bar */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-zinc-900/50 border border-white/5 rounded-lg px-4 py-3 flex items-center gap-3">
          <CalendarDays className="w-4 h-4 text-orange-400" />
          <div>
            <p className="text-white text-sm font-medium">{fmt(broadcastSummary?.broadcasts?.total)}</p>
            <p className="text-zinc-500 text-[10px]">Total Games</p>
          </div>
        </div>
        <div className="bg-zinc-900/50 border border-white/5 rounded-lg px-4 py-3 flex items-center gap-3">
          <Trophy className="w-4 h-4 text-emerald-400" />
          <div>
            <p className="text-white text-sm font-medium">{fmt(broadcastSummary?.broadcasts?.completed)}</p>
            <p className="text-zinc-500 text-[10px]">Completed</p>
          </div>
        </div>
        <div className="bg-zinc-900/50 border border-white/5 rounded-lg px-4 py-3 flex items-center gap-3">
          <Newspaper className="w-4 h-4 text-blue-400" />
          <div>
            <p className="text-white text-sm font-medium">{fmt(broadcastSummary?.articles)}</p>
            <p className="text-zinc-500 text-[10px]">Articles</p>
          </div>
        </div>
        <div className="bg-zinc-900/50 border border-white/5 rounded-lg px-4 py-3 flex items-center gap-3">
          <Bell className="w-4 h-4 text-violet-400" />
          <div>
            <p className="text-white text-sm font-medium">{fmt(broadcastSummary?.push_subscribers)}</p>
            <p className="text-zinc-500 text-[10px]">Push Subscribers</p>
          </div>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Trend Chart */}
        <div className="lg:col-span-2 bg-zinc-900/50 border border-white/5 rounded-lg p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-white font-bold text-sm flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-orange-400" /> Daily Activity
            </h3>
            <div className="flex items-center gap-3 text-[10px]">
              <span className="flex items-center gap-1"><span className="w-2 h-2 bg-orange-500 rounded-sm" /> Games</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 bg-blue-500 rounded-sm" /> New Users</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 bg-emerald-500 rounded-sm" /> Recordings</span>
            </div>
          </div>
          <TrendChart data={trend} height={140} />
        </div>

        {/* Peak Hours */}
        <div className="bg-zinc-900/50 border border-white/5 rounded-lg p-5">
          <h3 className="text-white font-bold text-sm flex items-center gap-2 mb-4">
            <Clock className="w-4 h-4 text-cyan-400" /> Game Times Distribution
          </h3>
          <PeakHoursChart hours={peakHours} />
          <p className="text-zinc-600 text-[10px] text-center mt-3">Based on actual scheduled broadcast times</p>
        </div>
      </div>

      {/* Bottom Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Top Content */}
        <div className="bg-zinc-900/50 border border-white/5 rounded-lg p-5">
          <h3 className="text-white font-bold text-sm flex items-center gap-2 mb-4">
            <Film className="w-4 h-4 text-orange-400" /> Top Content by Views
          </h3>
          {topContent.length === 0 ? (
            <p className="text-zinc-600 text-xs text-center py-6">No recordings yet</p>
          ) : (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {topContent.map((item, i) => (
                <div key={item.id || i} className="flex items-center gap-3 py-1.5 border-b border-white/5 last:border-0">
                  <span className="text-zinc-600 text-xs w-5">{i + 1}</span>
                  {item.thumbnail_url ? (
                    <img src={item.thumbnail_url} alt="" className="w-10 h-6 object-cover rounded flex-shrink-0" />
                  ) : (
                    <div className="w-10 h-6 bg-zinc-800 rounded flex-shrink-0" />
                  )}
                  <span className="text-zinc-300 text-xs truncate flex-1">{item.title || 'Untitled'}</span>
                  <span className="text-orange-400 text-xs font-medium">{fmt(item.view_count || 0)}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Sports Breakdown */}
        <div className="bg-zinc-900/50 border border-white/5 rounded-lg p-5">
          <h3 className="text-white font-bold text-sm flex items-center gap-2 mb-4">
            <Activity className="w-4 h-4 text-emerald-400" /> Content by Sport
          </h3>
          {sportsBreakdown.length === 0 ? (
            <p className="text-zinc-600 text-xs text-center py-6">No recordings yet</p>
          ) : (
            <MiniBarChart
              data={sportsBreakdown.map(s => ({ label: s.sport, value: s.total_views }))}
            />
          )}
        </div>

        {/* Ad Performance */}
        <div className="bg-zinc-900/50 border border-white/5 rounded-lg p-5">
          <h3 className="text-white font-bold text-sm flex items-center gap-2 mb-4">
            <MousePointerClick className="w-4 h-4 text-violet-400" /> Ad Performance
          </h3>
          {!adPerformance || adPerformance.summary?.total_ads === 0 ? (
            <p className="text-zinc-600 text-xs text-center py-6">No ads configured yet</p>
          ) : (
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-2 text-center">
                <div>
                  <p className="text-white text-lg font-bold">{fmt(adPerformance.summary?.total_ads)}</p>
                  <p className="text-zinc-500 text-[10px]">Total Ads</p>
                </div>
                <div>
                  <p className="text-white text-lg font-bold">{fmt(adPerformance.summary?.total_impressions)}</p>
                  <p className="text-zinc-500 text-[10px]">Impressions</p>
                </div>
                <div>
                  <p className="text-orange-400 text-lg font-bold">{adPerformance.summary?.ctr || 0}%</p>
                  <p className="text-zinc-500 text-[10px]">CTR</p>
                </div>
              </div>
              {adPerformance.by_placement?.length > 0 && (
                <div className="space-y-1 pt-2 border-t border-white/5">
                  <p className="text-zinc-500 text-[10px] font-medium uppercase">By Placement</p>
                  {adPerformance.by_placement.map((p, i) => (
                    <div key={i} className="flex items-center justify-between text-xs">
                      <span className="text-zinc-400 capitalize">{p.placement}</span>
                      <span className="text-zinc-300">{fmt(p.impressions)} imp / {p.ctr}% CTR</span>
                    </div>
                  ))}
                </div>
              )}
              {adPerformance.top_ads?.length > 0 && (
                <div className="space-y-1 pt-2 border-t border-white/5">
                  <p className="text-zinc-500 text-[10px] font-medium uppercase">Top Performing</p>
                  {adPerformance.top_ads.slice(0, 5).map((a, i) => (
                    <div key={i} className="flex items-center justify-between text-xs">
                      <span className="text-zinc-400 truncate max-w-[140px]">{a.name}</span>
                      <span className="text-zinc-300">{fmt(a.clicks)} clicks</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AnalyticsTab;
